import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FileText, Image, Eye, Trash2, Upload, Edit } from 'lucide-react';
import { toast } from 'sonner';
import ManualRedactionEditor from './ManualRedactionEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AccidentFiles({ accident, onUpdate, canManage, currentUserRole }) {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [redactionEditorOpen, setRedactionEditorOpen] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      for (let file of selectedFiles) {
        formData.append('files', file);
      }

      await axios.post(
        `${API}/accidents/${accident.id}/upload`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setSelectedFiles([]);
      document.getElementById('accident-file-upload-input').value = '';
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenRedactionEditor = async (fileIndex, fileName) => {
    try {
      // Get the file as IMAGE for redaction editor
      const response = await axios.get(
        `${API}/accidents/${accident.id}/files/${fileIndex}/image`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(response.data);
      setFileUrl(url);
      setSelectedFileIndex(fileIndex);
      setRedactionEditorOpen(true);
    } catch (error) {
      console.error('Failed to load file:', error);
      toast.error('Failed to load file for redaction');
    }
  };

  const handleSaveRedactions = async (redactions) => {
    try {
      await axios.post(
        `${API}/accidents/${accident.id}/files/${selectedFileIndex}/redact`,
        redactions,
        getAuthHeaders()
      );
      
      toast.success('File redacted successfully');
      if (onUpdate) onUpdate();
      
      // Clean up URL
      if (fileUrl) {
        window.URL.revokeObjectURL(fileUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save redactions');
      throw error;
    }
  };

  const handleFileDelete = async (fileIndex, fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;

    try {
      await axios.delete(
        `${API}/accidents/${accident.id}/files/${fileIndex}`,
        getAuthHeaders()
      );
      toast.success('File deleted successfully');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleFileView = async (fileIndex) => {
    try {
      const response = await axios.get(
        `${API}/accidents/${accident.id}/files/${fileIndex}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      if (!response.data || response.data.size === 0) {
        throw new Error('Empty file received');
      }

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
        toast.error('Pop-up blocked. Please allow pop-ups for this site.');
        window.URL.revokeObjectURL(url);
        return;
      }
      
      newWindow.onload = function() {
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      };
      
    } catch (error) {
      console.error('File View Error:', error);
      const errorMsg = error.response?.status === 404 
        ? 'File not found on server' 
        : error.response?.data?.detail 
        || error.message 
        || 'Failed to open file';
      
      toast.error(errorMsg);
    }
  };

  const attachments = accident.attachments || [];
  const getFileName = (attachment) => {
    return attachment.filename || 'document';
  };

  const getFileIcon = (attachment) => {
    const fileType = attachment.file_type || '';
    if (fileType.includes('image')) {
      return <Image className="h-4 w-4 text-orange-600" />;
    }
    return <FileText className="h-4 w-4 text-blue-600" />;
  };

  const isAdmin = currentUserRole === 'admin';

  return (
    <>
      <div className="space-y-2 mt-2" data-testid="accident-files">
        {attachments.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Attached Files:</p>
            {attachments.map((attachment, index) => (
              <div key={index} className="flex items-center justify-between bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded border border-orange-200 dark:border-orange-700">
                <div className="flex items-center space-x-2">
                  {getFileIcon(attachment)}
                  <span className="text-xs text-slate-700 dark:text-slate-300">{getFileName(attachment)}</span>
                  {attachment.redacted && (
                    <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-1 py-0.5 rounded">
                      Redacted
                    </span>
                  )}
                </div>
                <div className="flex space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2"
                    onClick={() => handleFileView(index)}
                    data-testid={`view-accident-file-${index}`}
                    title="View File"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  {isAdmin && attachment.s3_key && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-blue-600 dark:text-blue-400"
                      onClick={() => handleOpenRedactionEditor(index, getFileName(attachment))}
                      data-testid={`redact-accident-file-${index}`}
                      title="Manually Redact (Admin Only)"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                  {canManage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2"
                      onClick={() => handleFileDelete(index, getFileName(attachment))}
                      data-testid={`delete-accident-file-${index}`}
                      title="Delete File"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {canManage && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center space-x-2">
              <Input
                id="accident-file-upload-input"
                type="file"
                accept=".pdf,.jpg,.jpeg"
                multiple
                onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                className="flex-1"
                data-testid="add-accident-file-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleFileUpload}
                disabled={uploading || selectedFiles.length === 0}
                data-testid="upload-accident-file-button"
              >
                <Upload className="h-4 w-4 mr-1" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
            {selectedFiles.length > 0 && (
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {selectedFiles.length} file(s) ready: {selectedFiles.map(f => f.name).join(', ')}
              </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Upload photos (.jpg) or documents (.pdf) - Use manual redaction tool to remove sensitive data
            </p>
          </div>
        )}
      </div>

      {redactionEditorOpen && (
        <ManualRedactionEditor
          isOpen={redactionEditorOpen}
          onClose={() => {
            setRedactionEditorOpen(false);
            if (fileUrl) window.URL.revokeObjectURL(fileUrl);
          }}
          fileUrl={fileUrl}
          fileName={getFileName(attachments[selectedFileIndex])}
          fileIndex={selectedFileIndex}
          accidentId={accident.id}
          assessmentId={null}
          onSave={handleSaveRedactions}
        />
      )}
    </>
  );
}
